/**
 * Signup-policy read for the Capture login surface.
 *
 * TANDEM with Library's src/pages/LoginPage.jsx per
 * decisions/shared/2026-07-16-auth-surface-canonical-pattern.md — same endpoint,
 * same fail-closed semantics. Library inlines this in a useEffect; Capture
 * extracts it because capture-ui has no component-test harness (no
 * @testing-library/react), and the fail-closed branches are exactly what must be
 * covered by a test. Pure + injectable fetch, matching the other
 * src/utils/*.test.js modules.
 *
 * Endpoint: GET /api/library/config → library-backend GET /api/config
 * (backend/routes/config.js). Reached SAME-ORIGIN through the existing
 * /api/library proxy seam — Caddy on capture.steliad.com and src/setupProxy.js
 * in dev both rewrite /api/library → /api against port 5000. This is the same
 * seam Login.js already uses for /api/library/bootstrap. No CORS involved.
 */

// The one signup mode that may advertise "Create an account". Any other value
// ('allowlist', 'closed', an unrecognized future mode, or null) hides the link.
export const SIGNUP_MODE_OPEN = 'open';

export const SIGNUP_CONFIG_PATH = '/api/library/config';

/**
 * Read the server's signup mode. FAIL CLOSED: returns null on any error,
 * non-2xx, unparseable body, or missing/non-string signupMode field. null is
 * indistinguishable from a closed signup as far as the caller is concerned —
 * the link stays hidden. The server remains the real gate; this is cosmetic.
 *
 * @param {Function} [fetchImpl] fetch implementation (injectable for tests)
 * @returns {Promise<string|null>} the mode string, or null when unknown
 */
export async function fetchSignupMode(fetchImpl) {
  const doFetch =
    fetchImpl ||
    (typeof window !== 'undefined' && typeof window.fetch === 'function'
      ? window.fetch.bind(window)
      : null);
  if (!doFetch) return null;

  try {
    const res = await doFetch(SIGNUP_CONFIG_PATH);
    if (!res || !res.ok) return null;
    const body = await res.json();
    const mode = body?.signupMode;
    return typeof mode === 'string' && mode ? mode : null;
  } catch {
    // Network failure, blocked request, non-JSON body — all fail closed.
    return null;
  }
}

/**
 * Whether the "Create an account" link may be shown.
 * @param {string|null|undefined} signupMode
 * @returns {boolean}
 */
export function isSignupOpen(signupMode) {
  return signupMode === SIGNUP_MODE_OPEN;
}
